import { MESSAGE_API_URL } from '@/features/notifications/api';
import type {
  ComplaintInput,
  ComplaintResult,
  Conversation,
  ConversationPage,
  LinkPreview,
  Message,
  MessagePage,
  MessageStatus,
  MessageSync,
  MessageSyncEvent,
  ModerationSystemPayload,
  ReadMessagesResult,
  ReplyMessage,
  SendMessageInput,
  SendMessageResult,
} from './types';

type JsonObject = Record<string, unknown>;

export class MessageApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'MessageApiError';
  }
}

function object(value: unknown): JsonObject {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as JsonObject) : {};
}

function first(source: JsonObject, ...keys: string[]): unknown {
  for (const key of keys) {
    if (source[key] !== undefined) return source[key];
  }
  return undefined;
}

function stringValue(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function nullableString(value: unknown): string | null {
  if (typeof value === 'string' && value.length > 0) return value;
  if (typeof value === 'number') return String(value);
  return null;
}

function numberValue(value: unknown, fallback = 0): number {
  const converted = Number(value);
  return Number.isFinite(converted) ? converted : fallback;
}

function booleanValue(value: unknown, fallback = false): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function normalizeStatus(value: unknown): MessageStatus {
  const status = stringValue(value).toLowerCase();
  if (
    status === 'sending' ||
    status === 'pending' ||
    status === 'sent' ||
    status === 'delivered' ||
    status === 'read' ||
    status === 'not_delivered'
  ) {
    return status;
  }
  return 'sent';
}

function normalizeReply(value: unknown): ReplyMessage | null {
  const source = object(value);
  const sender = object(first(source, 'sender', 'author'));
  const id = nullableString(first(source, 'id', 'messageId', 'message_id'));
  if (!id) return null;
  return {
    id,
    senderId: numberValue(
      first(source, 'senderId', 'sender_id', 'authorId', 'author_id') ?? sender.id,
    ),
    senderName: stringValue(
      first(source, 'senderName', 'sender_name', 'authorName', 'author_name') ??
        first(sender, 'name', 'displayName', 'display_name', 'username'),
      'Пользователь',
    ),
    text: stringValue(first(source, 'text', 'textPreview', 'text_preview', 'snippet', 'preview')),
  };
}

function safeHttpUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
}

function normalizePreview(value: unknown): LinkPreview | null {
  const source = object(value);
  const url = safeHttpUrl(
    first(source, 'resolvedUrl', 'resolved_url', 'url', 'canonicalUrl', 'canonical_url'),
  );
  if (!url) return null;
  return {
    id: nullableString(source.id) ?? undefined,
    url,
    title: nullableString(source.title),
    description: nullableString(source.description),
    imageUrl: safeHttpUrl(first(source, 'imageUrl', 'image_url')),
    siteName: nullableString(first(source, 'siteName', 'site_name')),
    status: ['pending', 'ready', 'failed'].includes(stringValue(source.status))
      ? (source.status as LinkPreview['status'])
      : undefined,
  };
}

function normalizeSystemPayload(value: unknown): ModerationSystemPayload | null {
  const source = object(value);
  if (Object.keys(source).length === 0) return null;
  return {
    actionType: stringValue(first(source, 'actionType', 'action_type', 'action', 'type'), 'notice'),
    objectType: nullableString(first(source, 'objectType', 'object_type')),
    objectId: nullableString(first(source, 'objectId', 'object_id')),
    objectTitle: nullableString(first(source, 'objectTitle', 'object_title', 'title')),
    objectUrl: nullableString(first(source, 'objectUrl', 'object_url', 'url')),
    snapshot: nullableString(first(source, 'snapshot', 'textSnapshot', 'text_snapshot')),
    reason: nullableString(source.reason),
  };
}

export function normalizeMessage(value: unknown): Message {
  const source = object(value);
  const sender = object(first(source, 'sender', 'author'));
  const replyValue = first(source, 'replyTo', 'reply_to', 'reply');
  const previewValue = first(source, 'linkPreviews', 'link_previews', 'previews');
  const singularPreview = first(source, 'linkPreview', 'link_preview');
  const previews = Array.isArray(previewValue)
    ? previewValue.map(normalizePreview).filter((item): item is LinkPreview => item !== null)
    : singularPreview
      ? [normalizePreview(singularPreview)].filter((item): item is LinkPreview => item !== null)
      : [];
  const createdAt = stringValue(first(source, 'createdAt', 'created_at'), new Date().toISOString());
  const rawId = first(source, 'id', 'messageId', 'message_id');
  const clientMessageId = nullableString(first(source, 'clientMessageId', 'client_message_id'));
  const systemPayload = normalizeSystemPayload(
    first(source, 'systemPayload', 'system_payload', 'moderationPayload', 'moderation_payload'),
  );

  return {
    id: nullableString(rawId) ?? clientMessageId ?? crypto.randomUUID(),
    clientMessageId,
    conversationId: nullableString(first(source, 'conversationId', 'conversation_id')),
    senderId: numberValue(
      first(source, 'senderId', 'sender_id', 'authorId', 'author_id') ?? sender.id,
    ),
    recipientId: (() => {
      const id = first(source, 'recipientId', 'recipient_id');
      return id === undefined || id === null ? null : numberValue(id);
    })(),
    senderName: nullableString(
      first(source, 'senderName', 'sender_name', 'authorName') ??
        first(sender, 'name', 'displayName', 'display_name', 'username'),
    ),
    senderAvatarUrl: safeHttpUrl(
      first(source, 'senderAvatarUrl', 'sender_avatar_url', 'authorAvatarUrl') ??
        first(sender, 'avatarUrl', 'avatar_url'),
    ),
    text: stringValue(first(source, 'text', 'textPreview', 'text_preview', 'content', 'body')),
    replyToMessageId:
      nullableString(first(source, 'replyToMessageId', 'reply_to_message_id')) ??
      nullableString(first(object(replyValue), 'id', 'messageId', 'message_id')),
    replyTo: normalizeReply(replyValue),
    status: normalizeStatus(source.status),
    createdAt,
    deliveredAt: nullableString(first(source, 'deliveredAt', 'delivered_at')),
    readAt: nullableString(first(source, 'readAt', 'read_at')),
    sequence: (() => {
      const sequence = first(source, 'sequence', 'cursor');
      return sequence === undefined || sequence === null ? null : numberValue(sequence);
    })(),
    linkPreviews: previews,
    isSystem:
      booleanValue(first(source, 'isSystem', 'is_system')) ||
      stringValue(first(source, 'messageType', 'message_type')) === 'system' ||
      systemPayload !== null,
    systemPayload,
    error: nullableString(first(source, 'error', 'failureReason', 'failure_reason')),
  };
}

function normalizePeer(value: unknown, fallback: JsonObject = {}) {
  const source = object(value);
  const id = numberValue(first(source, 'id', 'userId', 'user_id', 'peerId', 'peer_id'));
  const rawProfile = first(source, 'profileUrl', 'profile_url');
  const profileUrl =
    typeof rawProfile === 'string' &&
    (rawProfile.startsWith('/') || /^https?:\/\//i.test(rawProfile))
      ? rawProfile
      : `/author/${id}`;
  return {
    id,
    name: stringValue(
      first(source, 'name', 'displayName', 'display_name', 'username'),
      stringValue(first(fallback, 'peerName', 'peer_name'), 'Пользователь'),
    ),
    avatarUrl: safeHttpUrl(first(source, 'avatarUrl', 'avatar_url', 'avatar')),
    profileUrl,
    isSystem: booleanValue(first(source, 'isSystem', 'is_system')),
  };
}

export function normalizeConversation(value: unknown): Conversation {
  const source = object(value);
  const block = object(first(source, 'block', 'blockState', 'block_state'));
  const peerValue = first(source, 'peer', 'otherUser', 'other_user', 'participant');
  const lastMessageValue = first(source, 'lastMessage', 'last_message');
  const kindValue = stringValue(first(source, 'kind', 'type')).toLowerCase();
  const blockedByMe = booleanValue(
    first(source, 'blockedByMe', 'blocked_by_me') ?? first(block, 'blockedByMe', 'blocked_by_me'),
  );
  const blockedByPeer = booleanValue(
    first(
      source,
      'blockedByPeer',
      'blocked_by_peer',
      'blockedByTarget',
      'blocked_by_target',
      'blockedMe',
      'blocked_me',
    ) ?? first(block, 'blockedByPeer', 'blocked_by_peer', 'blockedMe', 'blocked_me'),
  );
  const canSendValue = first(source, 'canSend', 'can_send');
  const lastMessage = lastMessageValue ? normalizeMessage(lastMessageValue) : null;

  return {
    id: nullableString(first(source, 'id', 'conversationId', 'conversation_id')),
    kind: kindValue === 'moderation' || kindValue === 'system' ? 'moderation' : 'p2p',
    peer: normalizePeer(peerValue, source),
    lastMessage,
    lastMessageAt:
      nullableString(
        first(source, 'lastMessageAt', 'last_message_at', 'updatedAt', 'updated_at'),
      ) ??
      lastMessage?.createdAt ??
      null,
    unreadCount: Math.max(0, numberValue(first(source, 'unreadCount', 'unread_count'))),
    isOnline: booleanValue(first(source, 'isOnline', 'is_online', 'online')),
    isTyping: booleanValue(first(source, 'isTyping', 'is_typing', 'typing')),
    blockedByMe,
    blockedByPeer,
    canSend: typeof canSendValue === 'boolean' ? canSendValue : !blockedByMe && !blockedByPeer,
    restrictionReason: nullableString(
      first(
        source,
        'restrictionReason',
        'restriction_reason',
        'sendRestriction',
        'send_restriction',
        'sendRestrictionReason',
        'send_restriction_reason',
      ),
    ),
  };
}

async function request<T>(path: string, accessToken: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${MESSAGE_API_URL}${path}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
    cache: 'no-store',
  });

  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const source = object(body);
    const rawMessage = first(source, 'message', 'error', 'detail');
    const message = Array.isArray(rawMessage)
      ? rawMessage.join('. ')
      : stringValue(rawMessage, `message-api request failed: ${response.status}`);
    throw new MessageApiError(message, response.status, nullableString(source.code) ?? undefined);
  }
  return body as T;
}

function pageSource(value: unknown): { rows: unknown[]; source: JsonObject } {
  const source = object(value);
  const nested = object(first(source, 'page', 'result'));
  const data = first(source, 'data', 'items', 'conversations', 'messages');
  const nestedData = first(nested, 'data', 'items');
  return {
    rows: Array.isArray(data) ? data : Array.isArray(nestedData) ? nestedData : [],
    source: Object.keys(nested).length > 0 ? { ...source, ...nested } : source,
  };
}

export async function getConversations(
  accessToken: string,
  options: { cursor?: string | null; limit?: number } = {},
): Promise<ConversationPage> {
  const search = new URLSearchParams({ limit: String(options.limit ?? 50) });
  if (options.cursor) search.set('cursor', options.cursor);
  const raw = await request<unknown>(`/api/v1/conversations?${search}`, accessToken);
  const { rows, source } = pageSource(raw);
  return {
    data: rows.map(normalizeConversation),
    nextCursor: nullableString(first(source, 'nextCursor', 'next_cursor')),
    hasMore: booleanValue(first(source, 'hasMore', 'has_more')),
  };
}

export async function getConversationForUser(
  accessToken: string,
  userId: number,
): Promise<Conversation> {
  const raw = await request<unknown>(`/api/v1/conversations/users/${userId}`, accessToken);
  const source = object(raw);
  const data = object(first(source, 'data'));
  const response = Object.keys(data).length > 0 ? data : source;
  return normalizeConversation(first(response, 'conversation') ?? first(response, 'draft') ?? raw);
}

export async function getConversationById(
  accessToken: string,
  conversationId: string,
): Promise<Conversation> {
  const raw = await request<unknown>(
    `/api/v1/conversations/${encodeURIComponent(conversationId)}`,
    accessToken,
  );
  const source = object(raw);
  return normalizeConversation(first(source, 'data', 'conversation') ?? raw);
}

export async function getMessages(
  accessToken: string,
  conversationId: string,
  options: { cursor?: string | null; limit?: number } = {},
): Promise<MessagePage> {
  const search = new URLSearchParams({ limit: String(options.limit ?? 50) });
  if (options.cursor) search.set('cursor', options.cursor);
  const raw = await request<unknown>(
    `/api/v1/conversations/${encodeURIComponent(conversationId)}/messages?${search}`,
    accessToken,
  );
  const { rows, source } = pageSource(raw);
  return {
    data: rows.map(normalizeMessage),
    nextCursor: nullableString(first(source, 'nextCursor', 'next_cursor')),
    hasMore: booleanValue(first(source, 'hasMore', 'has_more')),
  };
}

export async function sendMessage(
  accessToken: string,
  input: SendMessageInput,
): Promise<SendMessageResult> {
  const raw = await request<unknown>('/api/v1/messages', accessToken, {
    method: 'POST',
    body: JSON.stringify({
      ...(input.moderation
        ? { conversationId: input.conversationId }
        : { targetUserId: input.recipientId }),
      clientMessageId: input.clientMessageId,
      text: input.text,
      replyToMessageId: input.replyToMessageId || undefined,
    }),
  });
  const source = object(raw);
  const messageValue = first(source, 'message', 'data') ?? raw;
  const conversationValue = first(source, 'conversation');
  const message = normalizeMessage(messageValue);
  const conversation = conversationValue ? normalizeConversation(conversationValue) : null;
  return { message, conversation };
}

function normalizeReadMessagesResult(
  value: unknown,
  conversationId: string,
  fallbackReadIds: string[] = [],
): ReadMessagesResult {
  const source = object(value);
  const ids = first(
    source,
    'readIds',
    'read_ids',
    'readMessageIds',
    'read_message_ids',
    'messageIds',
    'message_ids',
  );
  const unread = first(
    source,
    'conversationUnreadCount',
    'conversation_unread_count',
    'unreadCount',
    'unread_count',
  );
  return {
    readIds: Array.isArray(ids) ? ids.map(String) : fallbackReadIds,
    conversationId:
      nullableString(first(source, 'conversationId', 'conversation_id')) ?? conversationId,
    unreadCount: unread === undefined ? undefined : Math.max(0, numberValue(unread)),
  };
}

export async function markMessagesRead(
  accessToken: string,
  conversationId: string,
  messageIds: string[],
): Promise<ReadMessagesResult> {
  const raw = await request<unknown>('/api/v1/messages/read', accessToken, {
    method: 'POST',
    body: JSON.stringify({
      conversationId,
      messageIds: [...new Set(messageIds)],
    }),
  });
  return normalizeReadMessagesResult(raw, conversationId, messageIds);
}

export async function markConversationRead(
  accessToken: string,
  conversationId: string,
): Promise<ReadMessagesResult> {
  const raw = await request<unknown>(
    `/api/v1/conversations/${encodeURIComponent(conversationId)}/read`,
    accessToken,
    { method: 'POST' },
  );
  return normalizeReadMessagesResult(raw, conversationId);
}

function normalizeSyncEvent(value: unknown): MessageSyncEvent {
  const source = object(value);
  const payload = first(source, 'payload', 'data') ?? value;
  const payloadSource = object(payload);
  const messageValue = first(source, 'message') ?? first(payloadSource, 'message');
  const conversationValue = first(source, 'conversation') ?? first(payloadSource, 'conversation');
  const type = stringValue(first(source, 'type', 'eventType', 'event_type'), 'message.created');
  return {
    type,
    sequence: numberValue(first(source, 'sequence', 'cursor')),
    conversationId:
      nullableString(first(source, 'conversationId', 'conversation_id')) ??
      nullableString(first(payloadSource, 'conversationId', 'conversation_id')),
    message: messageValue ? normalizeMessage(messageValue) : undefined,
    conversation: conversationValue ? normalizeConversation(conversationValue) : undefined,
    payload,
  };
}

export async function syncMessages(
  accessToken: string,
  after: number,
  limit = 500,
): Promise<MessageSync> {
  const search = new URLSearchParams({ after: String(after), limit: String(limit) });
  const raw = await request<unknown>(`/api/v1/messages/sync?${search}`, accessToken);
  const source = object(raw);
  const rows = first(source, 'data', 'events');
  return {
    data: Array.isArray(rows) ? rows.map(normalizeSyncEvent) : [],
    lastSequence: numberValue(first(source, 'lastSequence', 'last_sequence'), after),
    hasMore: booleanValue(first(source, 'hasMore', 'has_more')),
  };
}

export async function setUserBlocked(
  accessToken: string,
  userId: number,
  blocked: boolean,
): Promise<Conversation | null> {
  const raw = await request<unknown>(`/api/v1/users/${userId}/block`, accessToken, {
    method: blocked ? 'PUT' : 'DELETE',
  });
  const source = object(raw);
  const conversation = first(source, 'conversation', 'data');
  return conversation ? normalizeConversation(conversation) : null;
}

export async function createComplaint(
  accessToken: string,
  input: ComplaintInput,
): Promise<ComplaintResult> {
  const raw = await request<unknown>('/api/v1/complaints', accessToken, {
    method: 'POST',
    body: JSON.stringify({
      targetUserId: input.targetUserId,
      category: input.category,
      reason: input.reason,
      details: input.details || undefined,
    }),
  });
  const source = object(first(object(raw), 'data', 'complaint') ?? raw);
  return {
    id: nullableString(first(source, 'id', 'complaintId', 'complaint_id')) ?? '',
    status: stringValue(source.status, 'new'),
    createdAt: nullableString(first(source, 'createdAt', 'created_at')),
    alreadyExists: booleanValue(first(source, 'alreadyExists', 'already_exists', 'duplicate')),
  };
}
